import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../store/auth';
import { ApiError } from '../lib/api';
import { hapticError, hapticSuccess } from '../lib/haptics';

export default function LoginPage() {
  const login = useAuth((s) => s.login);
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await login(email.trim(), password);
      hapticSuccess();
      // Kapitel 3.2 (Screen 7): Erstanmeldung mit Filial-/Startpasswort →
      // zuerst ein eigenes Passwort festlegen.
      const loggedIn = useAuth.getState().user;
      navigate(loggedIn?.passwordIsInitial ? '/passwort-festlegen' : from, { replace: true });
    } catch (err) {
      hapticError();
      if (err instanceof ApiError) {
        setError(err.message || 'Login fehlgeschlagen');
      } else if (err instanceof Error) {
        setError(`Verbindungsfehler: ${err.message}`);
      } else {
        setError('Unbekannter Fehler');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="max-w-sm mx-auto mt-12">
      <h2 className="text-2xl font-bold text-center mb-6">Anmelden</h2>
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label htmlFor="email" className="block text-sm text-slate-300 mb-1">
            E-Mail
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 focus:outline-none focus:border-brand"
          />
        </div>
        <div>
          <label htmlFor="password" className="block text-sm text-slate-300 mb-1">
            Passwort
          </label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 focus:outline-none focus:border-brand"
            aria-describedby="password-help"
          />
          <p id="password-help" className="text-[11px] text-slate-500 mt-1">
            Erste Anmeldung? Nutze das Filialpasswort deines Standorts — danach legst du dein eigenes Passwort fest.
          </p>
        </div>
        {error && (
          <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded px-3 py-2">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={busy}
          className="w-full bg-brand hover:bg-brand-dark transition rounded py-2 font-medium disabled:opacity-50"
        >
          {busy ? 'Anmelden…' : 'Anmelden'}
        </button>
      </form>
      <p className="text-sm text-center mt-3">
        <Link to="/passwort-vergessen" className="text-brand-light hover:text-brand !min-h-0 underline">
          Passwort vergessen?
        </Link>
      </p>
      <p className="text-sm text-slate-400 text-center mt-4">
        Noch kein Konto?{' '}
        <Link to="/register" className="text-brand-light hover:text-brand !min-h-0 underline">
          Firma gründen oder beitreten
        </Link>
      </p>
    </div>
  );
}
